import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormCheckboxLabelPositions,
    DynamicFormCheckboxLabelPositionsNone,
} from "@midland-industries/Types/DynamicFormModels";
import { determineFormFieldIndexes } from "@midland-industries/Utilities/DetermineFormFieldIndexes";
import { determineFormIndex } from "@midland-industries/Utilities/DetermineFormIndex";
import { DynamicFormContext } from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import {
    DynamicFormCheckboxGroupContext,
    DynamicFormCheckboxGroupValidator,
} from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckboxGroup";
import React, { SyntheticEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    key = "key",
    group = "group",
    labelPosition = "labelPosition",
    label = "label",
    isRequired = "isRequired",
    isCheckedByDefault = "isCheckedByDefault",
    changeTrueFalseToYesNo = "changeTrueFalseToYesNo",
    hintText = "hintText",
}

export interface OwnProps {
    validateCheckboxClicked?: () => void;
    handleOnChange?: ((event: React.SyntheticEvent<Element, Event>, value: boolean, key: string) => void) | undefined;
    fields: {
        [fields.key]: string;
        [fields.group]: string;
        [fields.labelPosition]: DynamicFormCheckboxLabelPositions;
        [fields.label]: string;
        [fields.isCheckedByDefault]: boolean;
        [fields.isRequired]: boolean;
        [fields.changeTrueFalseToYesNo]: boolean;
        [fields.hintText]: string;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DynamicFormCheckboxStyles {
    wrapper?: InjectableCss;
    checkbox: CheckboxPresentationProps;
    checkboxError: CheckboxPresentationProps;
    checkboxLeft: CheckboxPresentationProps;
    checkboxLeftError: CheckboxPresentationProps;
    errorWrapper?: InjectableCss;
    error?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const dynamicFormCheckboxStyles: DynamicFormCheckboxStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-top: 0.5em;
            margin-bottom: 1em;
        `,
    },
    checkbox: {
        typographyProps: {
            css: css`
                margin-right: 10px;
            `,
        },
        css: css`
            span[role="checkbox"] {
                border-color: #ccc;
                border-width: 1px;
            }
            width: auto;
        `,
    },
    checkboxError: {
        typographyProps: {
            css: css`
                margin-right: 10px;
            `,
        },
        css: css`
            span[role="checkbox"] {
                border-color: #e64e25;
                border-width: 2px;
            }
            width: auto;
        `,
    },
    checkboxLeft: {
        typographyProps: {
            css: css`
                margin-right: 10px;
            `,
        },
        css: css`
            span[role="checkbox"] {
                border-color: #ccc;
                border-width: 1px;
            }
            width: auto;
            margin-left: 0;
        `,
    },
    checkboxLeftError: {
        typographyProps: {
            css: css`
                margin-right: 10px;
            `,
        },
        css: css`
            span[role="checkbox"] {
                border-color: #e64e25;
                border-width: 2px;
            }
            width: auto;
            margin-left: 0;
        `,
    },
    errorWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    error: {
        css: css`
            color: #e64e25;
            font-size: 15px;
            font-weight: 600;
        `,
    },
    tooltip: {
        cssOverrides: {
            tooltipClickable: css`
                margin-left: 8px;
            `,
        },
        typographyProps: {
            css: css`
                margin-left: 8px;
            `,
        },
    },
};

export const styles = dynamicFormCheckboxStyles;

const DynamicFormCheckbox: React.FC<Props> = ({
    fields,
    setDynamicFormFieldValue,
    dynamicFormData,
    validateCheckboxClicked,
    handleOnChange,
}) => {
    const { key, group, labelPosition, label, isCheckedByDefault, isRequired, changeTrueFalseToYesNo, hintText } =
        fields;

    const defaultSelectValue = !!isCheckedByDefault;

    const [isChecked, setIsChecked] = useState(defaultSelectValue);
    const [checkboxErrorMessage, setCheckboxErrorMessage] = useState("");
    const [fieldIndex, setFieldIndex] = useState(-1);
    const [formIndex, setFormIndex] = useState(-1);

    const dynamicFormContext = useContext(DynamicFormContext);
    const dynamicFormCheckboxGroupContext = useContext(DynamicFormCheckboxGroupContext);

    const isStandAlone = dynamicFormCheckboxGroupContext.groupKey === "";

    /**
     * This useEffect watches for changes to the forms array in the CustomDynamicFormState.
     * When triggered, the form has been added and the widget is safe to register itself
     * into the CustomDynamicFormState.
     */
    useEffect(() => {
        initializeCheckboxWidget();
    }, [dynamicFormData?.forms]);

    /**
     * This useEffect watches for changes to the local useState property isChecked value.
     * When the value change, the individual DynamicFormCheckbox widget will be validated
     * and those results updated into the CustomDynamicFormState.
     * This also triggers the validateCheckboxClicked function, which is passed in from
     * the DynamicFormCheckboxGroup. If provided, this function triggers the parent
     * DynamicFormCheckboxGroup to validatate itself.
     */
    useEffect(() => {
        const { isValid, errorMessage } = validateCheckboxValue();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: group || "",
                label: labelPosition === DynamicFormCheckboxLabelPositionsNone ? key : label,
                isRequired: false,
                value: isChecked,
                type: "DynamicFormCheckbox",
                changeTrueFalseToYesNo,
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
        if (typeof validateCheckboxClicked !== "undefined") {
            validateCheckboxClicked();
        }
    }, [isChecked]);

    /**
     * This useEffect watches for changes to this field in the CustomDynamicFormState.
     * If the value in the CustomDynamicFormState changes, the DynamicFormCheckbox widget
     * will change its value to match.
     */
    useEffect(() => {
        if (isChecked !== dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value) {
            if (dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value === undefined) {
                setIsChecked(defaultSelectValue);
            } else {
                setIsChecked(dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value);
            }
        }
    }, [dynamicFormData?.forms[formIndex]?.fields[fieldIndex]]);

    /**
     * This function:
     * * sets the formIndex and fieldIndex values for this widget;
     * * validates the initial state of the widget;
     * * adds the widget to the CustomDynamicFormState using initial values;
     * * triggers the validateCheckboxClicked function, which is passed in from the
     *   DynamicFormCheckboxGroup. If provided, this function triggers the parent
     *   DynamicFormCheckboxGroup to validatate itself.
     */
    const initializeCheckboxWidget = () => {
        const formFieldIndexes = determineFormFieldIndexes(dynamicFormContext.formName, key, dynamicFormData);
        setFormIndex(formFieldIndexes.formIndex);
        setFieldIndex(formFieldIndexes.fieldIndex);

        // No form registered in state
        if (formFieldIndexes.formIndex < 0) {
            return;
        }

        // Widget already registered in state
        if (formFieldIndexes.formIndex > -1 && formFieldIndexes.fieldIndex > -1) {
            return;
        }

        const { isValid, errorMessage } = validateCheckboxValue();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: group || "",
                label: labelPosition === DynamicFormCheckboxLabelPositionsNone ? key : label,
                isRequired: false,
                value: defaultSelectValue,
                type: "DynamicFormCheckbox",
                changeTrueFalseToYesNo,
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });

        if (typeof validateCheckboxClicked !== "undefined") {
            validateCheckboxClicked();
        }
    };

    /**
     * This function adds the DynamicFormCheckboxGroupValidator into the dynamicFormCheckboxGroupContext.
     * It dose nothing if when the DynamicFormCheckbox is in StandAlone mode.
     */
    const triggerDynamicFormCheckboxGroupValidator = () => {
        if (isStandAlone) {
            return;
        }

        const dynamicFormGroupCheckboxValidator: DynamicFormCheckboxGroupValidator = {
            fieldKey: key,
            validate: validateGroupCheckbox,
        };

        const existingValidatorIndex = dynamicFormCheckboxGroupContext.validators.findIndex(v => v.fieldKey === key);
        if (existingValidatorIndex < 0) {
            dynamicFormCheckboxGroupContext.validators.push(dynamicFormGroupCheckboxValidator);
        } else {
            dynamicFormCheckboxGroupContext.validators[existingValidatorIndex] = dynamicFormGroupCheckboxValidator;
        }
    };

    const validateCheckboxValue = () => {
        if (isStandAlone) {
            const { isValid, errorMessage } = validateStandAloneCheckbox();
            return {
                isValid,
                errorMessage,
            };
        }
        // DynamicFormCheckboxGroup member validation
        const { isValid, errorMessage } = validateGroupCheckbox();
        return {
            isValid,
            errorMessage,
        };
    };

    /**
     * This function provides validation for a stand-alone DynamicFormCheckbox widget.
     */
    const validateStandAloneCheckbox = () => {
        let errorMessage = "";

        // validate: isRequired
        if (isRequired && !isChecked) {
            errorMessage = siteMessage("Field_Required", labelPosition === "none" ? key : label) as string;
        }

        // update checkboxErrorMessage if necessary
        if (errorMessage !== checkboxErrorMessage) {
            setCheckboxErrorMessage(errorMessage);
        }

        return {
            isValid: !errorMessage,
            errorMessage,
        };
    };

    /**
     * This function provides validation for a DynamicFormCheckbox widget created using a DynamicFormCheckboxGroup.
     * There are currently no validation requirements for the DynamicFormCheckbox in 'group' mode.
     * However, the current value is returned and needed by the DynamicFormCheckboxGroupContext.
     */
    const validateGroupCheckbox = () => {
        triggerDynamicFormCheckboxGroupValidator();
        return {
            isValid: true,
            isChecked,
            errorMessage: "",
        };
    };

    const checkboxChangeHandler = (event: SyntheticEvent<Element, Event>, value: boolean) => {
        const currValue = isChecked;
        setIsChecked(!currValue);
        if (typeof handleOnChange !== "undefined") {
            handleOnChange(event, value, key);
        }
    };

    const getDisplayValidationErrors = () => {
        const formIndex = determineFormIndex(dynamicFormContext.formName, dynamicFormData).formIndex;
        if (formIndex < 0) {
            return false;
        }

        return dynamicFormData?.forms[formIndex]?.displayValidationErrors;
    };

    const getGroupCheckboxStyle = () => {
        const displayValidationErrors = getDisplayValidationErrors();
        if (displayValidationErrors && checkboxErrorMessage) {
            switch (labelPosition) {
                case "right":
                    return styles.checkboxError;
                case "left":
                    return styles.checkboxLeftError;
                case "none":
                    return styles.checkboxError;
                default:
                    return styles.checkboxError;
            }
        } else {
            switch (labelPosition) {
                case "right":
                    return styles.checkbox;
                case "left":
                    return styles.checkboxLeft;
                case "none":
                    return styles.checkbox;
                default:
                    return styles.checkbox;
            }
        }
    };

    if (typeof document !== "undefined") {
        const currentWidget = document.getElementById(key) as HTMLInputElement;
        if (currentWidget) {
            currentWidget.addEventListener("keydown", function (e) {
                if (e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                }
            });
        }
    }

    if (group) {
        return (
            <Checkbox
                {...getGroupCheckboxStyle()}
                checked={isChecked}
                uid={key}
                labelPosition={
                    labelPosition !== "none"
                        ? labelPosition === "right"
                            ? labelPosition
                            : labelPosition === "left"
                            ? labelPosition
                            : undefined
                        : undefined
                }
                onChange={checkboxChangeHandler}
            >
                {labelPosition !== "none" ? label : undefined}
            </Checkbox>
        );
    }

    const getStandAloneCheckboxStyles = () => {
        const displayValidationErrors = getDisplayValidationErrors();
        if (displayValidationErrors && checkboxErrorMessage) {
            switch (labelPosition) {
                case "right":
                    return styles.checkboxError;
                case "left":
                    return styles.checkboxLeftError;
                case "none":
                    return styles.checkboxError;
                default:
                    return styles.checkboxError;
            }
        } else {
            switch (labelPosition) {
                case "right":
                    return styles.checkbox;
                case "left":
                    return styles.checkboxLeft;
                case "none":
                    return styles.checkbox;
                default:
                    return styles.checkbox;
            }
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Checkbox
                {...getStandAloneCheckboxStyles()}
                uid={key}
                checked={isChecked}
                labelPosition={
                    labelPosition !== "none"
                        ? labelPosition === "right"
                            ? labelPosition
                            : labelPosition === "left"
                            ? labelPosition
                            : undefined
                        : undefined
                }
                onChange={checkboxChangeHandler}
            >
                {labelPosition !== "none" ? (isRequired ? `${label} *` : label) : undefined}
            </Checkbox>
            {hintText && hintText.length > 0 && <Tooltip {...styles.tooltip} text={hintText} />}
            {getDisplayValidationErrors() && checkboxErrorMessage && (
                <StyledWrapper {...styles.errorWrapper}>
                    <Typography {...styles.error}>{checkboxErrorMessage}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicFormCheckbox);
